import '../../styles/restScreens.css';
import { useState, useEffect, useRef } from 'react';
import { fetchPhotoSuggestions, uploadContactPhoto } from '../../lib/contactEnrichment';
import { firstEmail } from './clientsConstants';

/**
 * Module 2 CRM — Contact Photo Auto-Suggest Enrichment Panel (BL-150 / BL-193 Gate 3).
 *
 * Provides:
 * 1. Gravatar-by-email baseline enrichment + secondary enrichment suggestions.
 * 2. Suggest-and-confirm UX: Suggestions are NEVER auto-applied to the contact record.
 *    Staff must explicitly confirm/accept a suggestion before it is populated into the draft.
 * 3. Manual upload fallback: Staff can upload a photo file directly or specify a custom URL.
 */
export default function PhotoEnrichmentPanel({
    contact = null,
    account = null,
    currentPhotoUrl = '',
    onConfirmPhoto = () => {},
    onClearPhoto = () => {},
    supabaseClient = null,
}) {
    const email = firstEmail(contact) || '';
    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [customUrlInput, setCustomUrlInput] = useState('');
    const [statusNotice, setStatusNotice] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const fileInputRef = useRef(null);

    // Auto-fetch suggestions when panel opens or email changes
    useEffect(() => {
        if (!email) {
            setSuggestions([]);
            return;
        }
        setLoadingSuggestions(true);
        try {
            const results = fetchPhotoSuggestions({
                email,
                name: contact?.name || '',
                accountName: account?.account_name || ''
            });
            setSuggestions(results);
        } catch (e) {
            console.error('Failed to fetch photo suggestions:', e);
        } finally {
            setLoadingSuggestions(false);
        }
    }, [email, contact?.name, account?.account_name]);

    const handleConfirmSuggestion = (suggestion) => {
        onConfirmPhoto(suggestion.url);
        setStatusNotice(`Selected ${suggestion.source} photo. Click Save on the card to apply to record.`);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setStatusNotice('');
        try {
            const { url } = await uploadContactPhoto(file, supabaseClient, contact?.id);
            onConfirmPhoto(url);
            setStatusNotice('Uploaded custom photo. Click Save on the card to apply to record.');
        } catch (err) {
            setStatusNotice(`Upload failed: ${err.message || String(err)}`);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleApplyCustomUrl = () => {
        const trimmed = customUrlInput.trim();
        if (!trimmed) return;
        onConfirmPhoto(trimmed);
        setStatusNotice('Custom photo URL applied. Click Save on the card to apply to record.');
        setCustomUrlInput('');
    };

    const handleClearPhoto = () => {
        onClearPhoto();
        setStatusNotice('Photo removed. Click Save on the card to apply changes.');
    };

    const initials = (contact?.name || '?')
        .split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

    return (
        <div data-m6-layout="clients" data-m6-component="PhotoEnrichmentPanel" className="my-3 space-y-4 rounded-lg border border-[var(--surface-border)] bg-[var(--surface-sunken)] p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--surface-border)] pb-2">
                <div className="flex items-center gap-3">
                    {currentPhotoUrl ? (
                        <img
                            src={currentPhotoUrl}
                            alt={contact?.name || 'Contact'}
                            className="h-12 w-12 rounded-lg border border-[var(--surface-border)] object-cover"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = ''; // Fallback image handling
                            }}
                        />
                    ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-[var(--selected-pill-border)] bg-[var(--nav-active-bg)] text-sm font-semibold text-[var(--accent-ink)]">
                            {initials}
                        </div>
                    )}
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink)]">
                            Contact Photo & Enrichment
                        </div>
                        <div className="text-xs text-[var(--ink-muted)]">
                            {currentPhotoUrl ? (
                                <span className="font-medium text-[var(--accent-ink)]">Photo assigned (draft)</span>
                            ) : (
                                <span>No photo set (using initials)</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    {email && (
                        <button
                            type="button"
                            onClick={() => setShowSuggestions(!showSuggestions)}
                            className="rounded border border-[var(--selected-pill-border)] bg-[var(--nav-active-bg)] px-3 py-1.5 text-xs font-medium text-[var(--accent-ink)] transition hover:opacity-80"
                        >
                            {showSuggestions ? 'Hide Suggestions' : 'Auto-Suggest Photos'}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="rounded border border-[var(--surface-border)] px-3 py-1.5 text-xs font-medium text-[var(--ink)] transition hover:bg-[var(--surface-sunken)] disabled:opacity-50"
                    >
                        {uploading ? 'Uploading…' : 'Upload File'}
                    </button>
                    {currentPhotoUrl && (
                        <button
                            type="button"
                            onClick={handleClearPhoto}
                            className="rounded border border-red-500/30 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-500/10"
                        >
                            Remove
                        </button>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                    />
                </div>
            </div>

            {statusNotice && (
                <div className="flex items-center justify-between rounded border border-[var(--surface-border)] bg-[var(--surface-raised)] px-3 py-2 text-xs text-[var(--ink)]">
                    <span>{statusNotice}</span>
                    <button onClick={() => setStatusNotice('')} className="ml-2 text-[var(--ink-muted)] hover:text-[var(--ink)]">×</button>
                </div>
            )}

            {/* Suggestions panel */}
            {showSuggestions && (
                <div className="space-y-2 pt-1">
                    <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-muted)]">
                        <span>Suggested Photos (Suggest-and-Confirm)</span>
                        <span className="text-[10px] font-normal normal-case text-[var(--ink-muted)]">Click "Confirm" to apply</span>
                    </div>

                    {!email ? (
                        <div className="py-2 text-xs italic text-[var(--ink-muted)]">
                            Add a contact email to search Gravatar and enrichment APIs.
                        </div>
                    ) : loadingSuggestions ? (
                        <div className="py-2 text-xs text-[var(--ink-muted)]">Searching photo providers…</div>
                    ) : suggestions.length === 0 ? (
                        <div className="py-2 text-xs text-[var(--ink-muted)]">No photo suggestions found for {email}.</div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {suggestions.map((sug) => {
                                const isSelected = currentPhotoUrl === sug.url;
                                return (
                                    <div
                                        key={sug.id}
                                        className={`p-2.5 rounded-lg border transition flex items-center gap-3 ${
                                            isSelected
                                                ? 'bg-[var(--nav-active-bg)] border-[var(--selected-pill-border)]'
                                                    : 'bg-[var(--surface-canvas)] border-[var(--surface-border)] hover:border-[var(--selected-pill-border)]'
                                        }`}
                                    >
                                        <img
                                            src={sug.url}
                                            alt={sug.source}
                                            className="h-10 w-10 flex-shrink-0 rounded-md bg-[var(--surface-sunken)] object-cover"
                                            onError={(e) => {
                                                // Hide or style broken image suggestion
                                                e.target.style.opacity = '0.3';
                                            }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <span className="truncate text-xs font-semibold text-[var(--ink)]">
                                                    {sug.source}
                                                </span>
                                                <span className="rounded bg-[var(--surface-raised)] px-1.5 py-0.2 text-[10px] text-[var(--ink-muted)]">
                                                    {sug.badge}
                                                </span>
                                            </div>
                                            <div className="truncate text-[11px] text-[var(--ink-muted)]">
                                                {sug.description}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleConfirmSuggestion(sug)}
                                            className={`px-2.5 py-1 rounded text-xs font-medium flex-shrink-0 transition ${
                                                isSelected
                                                    ? 'bg-[var(--btn-fill)] text-[var(--btn-ink)] cursor-default'
                                                    : 'bg-[var(--surface-raised)] text-[var(--ink)] hover:bg-[var(--btn-fill)] hover:text-[var(--btn-ink)]'
                                            }`}
                                        >
                                            {isSelected ? 'Confirmed ✓' : 'Confirm'}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Custom URL Fallback */}
            <div className="flex items-center gap-2 border-t border-[var(--surface-border)] pt-2">
                <input
                    type="url"
                    value={customUrlInput}
                    onChange={(e) => setCustomUrlInput(e.target.value)}
                    placeholder="Or paste direct image URL (https://...)"
                    className="flex-1 rounded border border-[var(--surface-border)] bg-[var(--surface-canvas)] px-2.5 py-1 text-xs text-[var(--ink)] placeholder:text-[var(--ink-muted)] outline-none focus:border-[var(--selected-pill-border)]"
                />
                <button
                    type="button"
                    onClick={handleApplyCustomUrl}
                    disabled={!customUrlInput.trim()}
                    className="rounded border border-[var(--surface-border)] px-3 py-1 text-xs font-medium text-[var(--ink)] transition hover:bg-[var(--surface-raised)] disabled:opacity-40"
                >
                    Apply URL
                </button>
            </div>
        </div>
    );
}
